import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AssigneeIcon from "./AssigneeIcon";
import { useNavigate } from "react-router-dom";
import SourceTypeDropdown from "./SourceTypeDropdown";
import ILeadData, { IRoles, ILeadUser } from "../../models/LeadData.model";
import ToggleSwitch from "../Users/ToggleSwitch";
import {
  setUpdateOutsideModal,
  updateLeadData,
} from "../../store/slices/leadSlice";
import toast from "react-hot-toast";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteKanbanLead,
  updateCardField,
} from "../../store/slices/kanbanSlice";
import { Trash2 } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";

interface SortableCardProps {
  card: ILeadData;
  index: number;
  columnId: string;
  updateSourceType: (leadId: string, sourceType: string) => void;
  updateAssignee: (
    leadId: string,
    oldAssigneeId: string,
    newAssignee: string,
    index: number,
    columnIndex: string,
  ) => void;
  roles: IRoles;
}

const SortableCard: React.FC<SortableCardProps> = ({
  card,
  index,
  columnId,
  updateSourceType,
  updateAssignee,
  roles,
}) => {
  const navigate = useNavigate();
  const dispatch: RootDispatch = useDispatch();
  const role = useSelector((state: RootState) => state.auth.role);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const [selectedAssignee, setSelectedAssignee] = useState<string>("");
  const [showToTelecaller, setShowToTelecaller] = useState<boolean>(
    card?.showToTelecaller ?? false,
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: card._id!,
      data: { card, columnId },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    if (dropdownOpen === true) {
      setDropdownOpen(false);
    } else {
      setDropdownOpen(true);
    }
    e.stopPropagation();
  };

  const handleToggleShowToTelecaller = useCallback(
    async (checked: boolean) => {
      setShowToTelecaller(checked);
      try {
        dispatch(setUpdateOutsideModal());
        await dispatch(
          updateLeadData({
            _id: card._id!,
            data: { showToTelecaller: checked },
          }),
        ).unwrap();

        dispatch(
          updateCardField({
            leadId: card._id!,
            patch: { showToTelecaller: checked },
          }),
        );

        toast.success("Updated visibility for telecaller");
      } catch (err) {
        setShowToTelecaller((prev) => !prev);
        toast.error("Failed to update visibility");
      }
    },
    [dispatch, card._id],
  );

  const getStateDistrictText = () => {
    if (!card?.address) return "";
    const { state, district } = card.address;
    return `${state || ""}${district ? ` - ${district}` : ""}`;
  };

  const handleUpdateAssignee = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedAssignee) {
      updateAssignee(
        card._id!,
        card.assignee?._id || "",
        selectedAssignee,
        index,
        columnId,
      );
      setDropdownOpen(false);
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      await dispatch(deleteKanbanLead(leadId)).unwrap();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style }}
      {...attributes}
      className="w-full h-full  bg-white rounded-lg shadow-lg hover:shadow-xl border border-gray-200 overflow-visible z-10"
      onClick={() => navigate(`/lead-details/${card._id}`, { state: { card } })}
    >
      <div className="flex flex-col gap-1 justify-between h-full p-3">
        <div>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-800 truncate">
              {card.name.capitalizeWords()}
            </div>

            <div className="flex items-center gap-2">
              {card.followUpCount !== undefined && (
                <span className="text-[11px] font-bold text-gray-400 bg-gray-50 px-1.5 rounded border border-gray-100">
                  {card.followUpCount}
                </span>
              )}
              {(role === "admin" || role === "sh" || role === "bdm") && (
                <button
                  className="p-1 rounded hover:bg-red-600 hover:text-white text-red-500 duration-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                  }}
                >
                  <Trash2 size={14} />
                </button>
              )}
              <div
                {...listeners}
                onClick={(e) => e.stopPropagation()}
                className="p-1 cursor-grab active:cursor-grabbing group-hover:opacity-100 transition-opacity duration-200"
              >
                <svg
                  className="w-4 h-4 text-gray-800"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 5h2M9 12h2M9 19h2M14 5h2M14 12h2M14 19h2" />
                </svg>
              </div>
            </div>
          </div>
          {showDeleteConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center  bg-opacity-50">
              <div className="bg-white rounded-lg shadow-lg p-4 w-72">
                <h2 className="text-sm font-semibold text-gray-800">
                  Confirm Delete
                </h2>
                <p className="text-xs text-gray-600 mt-2">
                  Are you sure you want to delete this lead?
                </p>

                <div className="flex justify-end gap-3 mt-4">
                  <Button
                    type={ButtonType.Cancel}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(false);
                    }}
                    className="text-xs !px-4 !py-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type={ButtonType.Error}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLead(card._id!);
                      setShowDeleteConfirm(false);
                    }}
                    className="text-xs !px-4 !py-1"
                  >
                    Yes
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div
            className="flex items-center"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {getStateDistrictText() && (
              <div className="text-xs text-gray-500 mt-1 truncate">
                {getStateDistrictText().capitalizeWords()}
              </div>
            )}

            <div className="flex shrink-0 ml-auto">
              <ToggleSwitch
                id={`show-to-tele-${card._id}`}
                checked={showToTelecaller}
                onChange={handleToggleShowToTelecaller}
                label=""
                description=""
                showIcon={false}
                containerClassName="flex items-center justify-end p-0 bg-transparent border-0 mt-1"
                sizeVariant="xs"
              />
            </div>
          </div>
          {card.meetingDone && (
            <div className="inline-flex items-center gap-1 text-xs font-semibold  text-emerald-700">
              <span>Meeting Completed</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center">
          <SourceTypeDropdown
            currentType={card.type ?? ""}
            onUpdate={(newType) => updateSourceType(card._id!, newType)}
          />

          <div onClick={toggleDropdown} ref={wrapperRef}>
            <AssigneeIcon assignee={card.assignee} onClick={() => {}} />
          </div>
        </div>

        {dropdownOpen && (
          <div
            className="absolute top-1 right-20  mt-2 p-2 bg-white border rounded shadow text-xs z-30"
            onClick={(e) => e.stopPropagation()}
          >
            <select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="w-full p-1 border rounded"
            >
              <option value="">Select Assignee</option>
              <option
                key={currentUser?._id || currentUser?.id}
                value={currentUser?._id || currentUser?.id}
              >
                Self
              </option>
              {roles.userBasedOnRoles?.map((role: ILeadUser) => (
                <option key={role._id} value={role._id}>
                  {role.username}
                </option>
              ))}
              {roles.manager && (
                <option key={roles.manager._id} value={roles.manager._id}>
                  {roles.manager.username}
                </option>
              )}
            </select>
            <button
              className="mt-2 w-full bg-blue-500 text-white py-1 rounded"
              onClick={handleUpdateAssignee}
            >
              Assign
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SortableCard;
